
import React, { useCallback, useEffect, useState } from 'react';
import { KEYBOARD_KEYS, LetterEvaluation } from '@/utils/wordUtils';
import { cn } from '@/lib/utils';
import { useTheme } from 'next-themes';
import { GameMode, GameStatus } from '@/utils/gameState';
import { shouldShowHintLetter } from '@/utils/gameUtils';
import { useGame } from '@/context/GameContext';
import { useToast } from '@/hooks/use-toast';
import { evaluateGuess, isValidWord } from '@/utils/wordUtils';
import { GAME_SETTINGS } from '@/utils/gameState';
import { trackEvent } from '@/utils/analytics';

interface KeyboardProps {
  keyboardState: Record<string, LetterEvaluation | undefined>;
  hintLetter?: string | null;
  gameMode: GameMode;
}

const Keyboard: React.FC<KeyboardProps> = ({ 
  keyboardState,
  gameMode,
  hintLetter
}) => {
  const { resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === 'dark';
  const { toast } = useToast();
  const {
    gameState,
    setGameState,
    currentGuess,
    setCurrentGuess,
    revealAnimationComplete,
    setRevealAnimationComplete,
    setTimerActive,
    setShowResult,
    setGameConfig,
    initializeGame
  } = useGame();

  const [isKeyboardDisabled, setIsKeyboardDisabled] = useState(true);
  const isGameComplete = gameState.gameStatus !== GameStatus.IN_PROGRESS;
  const isRandomMode = gameMode === GameMode.RANDOM;

  useEffect(() => {
    setIsKeyboardDisabled(gameState.gameStatus !== GameStatus.IN_PROGRESS || !revealAnimationComplete);
  }, [gameState.gameStatus, revealAnimationComplete]);

  // Handle key presses on desktops
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (gameState.gameStatus !== GameStatus.IN_PROGRESS || !revealAnimationComplete) return;
      const key = event.key.toLowerCase();
      
      if (key === 'enter') {
        handleSubmitGuess();
      } else if (key === 'backspace') {
        handleDeleteLetter();
      } else if (/^[a-z]$/.test(key)) {
        handleAddLetter(key);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState.gameStatus, gameState.currentRowIndex, currentGuess, revealAnimationComplete, gameMode]);

  const handleAddLetter = useCallback((letter: string) => {
    if (currentGuess.length < GAME_SETTINGS.wordLength) {
      setCurrentGuess(prev => prev + letter);
    }
  }, [currentGuess.length, setCurrentGuess]);
  
  const handleDeleteLetter = useCallback(() => {
    setCurrentGuess(prev => prev.slice(0, -1));
  }, [setCurrentGuess]);
  
  const animateRowShake = () => {
    const gridElement = document.querySelector('.grid');
    if (gridElement) {
      const currentRow = gridElement.children[gameState.currentRowIndex];
      currentRow?.classList.add('animate-shake');
      setTimeout(() => {
        currentRow?.classList.remove('animate-shake');
      }, 500);
    }
  };
  
  const updateKeyboardState = (evaluation: ('correct' | 'present' | 'absent')[]) => {
    const newKeyboardState = { ...gameState.keyboardState };
    
    for (let i = 0; i < currentGuess.length; i++) {
      const letter = currentGuess[i];
      const currentEval = evaluation[i];
      const existingEval = newKeyboardState[letter];
      
      if (!existingEval || 
          (existingEval === 'absent' && currentEval !== 'absent') ||
          (existingEval === 'present' && currentEval === 'correct')) {
        newKeyboardState[letter] = currentEval;
      }
    }
    
    return newKeyboardState;
  };
  
  const handleSubmitGuess = useCallback(() => {
    const rowIndex = gameState.currentRowIndex + 1;

    if (currentGuess.length !== GAME_SETTINGS.wordLength) {
      trackEvent('guess_rejected', {
        reason: 'not_enough_letters',
        row_index: rowIndex,
        guess_length: currentGuess.length,
        game_mode: gameMode
      });

      toast({
        title: 'Not enough letters',
        variant: 'destructive'
      });
      
      animateRowShake();
      return;
    }
    
    if (!isValidWord(currentGuess)) {
      trackEvent('guess_rejected', {
        reason: 'not_in_word_list',
        row_index: rowIndex,
        guess_length: currentGuess.length,
        game_mode: gameMode
      });

      toast({
        title: 'Not in word list',
        variant: 'destructive'
      });
      
      animateRowShake();
      return;
    }

    trackEvent('guess_submitted', {
      row_index: rowIndex,
      game_mode: gameMode
    });
    
    setRevealAnimationComplete(false);
    
    const evaluation = evaluateGuess(currentGuess, gameState.targetWord);
    const newKeyboardState = updateKeyboardState(evaluation);
    
    const newGuesses = [...gameState.guesses, currentGuess];
    const newEvaluations = [...gameState.evaluations, evaluation];
    
    const isWin = evaluation.every(result => result === 'correct');
    const isLose = !isWin && newGuesses.length >= GAME_SETTINGS.maxAttempts;
    
    let newGameStatus = gameState.gameStatus;
    if (isWin) {
      newGameStatus = GameStatus.WIN;
    } else if (isLose) {
      newGameStatus = GameStatus.LOSE;
    }
    
    setGameState({
      ...gameState,
      guesses: newGuesses,
      evaluations: newEvaluations,
      gameStatus: newGameStatus,
      currentRowIndex: gameState.currentRowIndex + 1,
      keyboardState: newKeyboardState,
      lastCompletedTime: isWin || isLose ? Date.now() : null
    });
    
    setCurrentGuess('');
    setTimerActive(false);
  }, [currentGuess, gameState, gameMode, toast, setRevealAnimationComplete, setGameState, setCurrentGuess, setTimerActive]);
  
  const handleKeyPress = useCallback((key: string) => {
    if (key === 'enter') {
      handleSubmitGuess();
    } else if (key === 'backspace') {
      handleDeleteLetter();
    } else {
      handleAddLetter(key);
    }
  }, [handleSubmitGuess, handleDeleteLetter, handleAddLetter]);

  // Handler for keyboard button clicks
  const handleClick = useCallback((key: string) => {
    if (!isKeyboardDisabled) {
      handleKeyPress(key);
    }
  }, [isKeyboardDisabled, handleKeyPress]);

  const handleShowResults = useCallback(() => {
    setShowResult(true);
  }, [setShowResult]);

  const handleNewRandomGame = useCallback(() => {
    const newConfig = { mode: GameMode.RANDOM, date: null };
    setGameConfig(newConfig);
    initializeGame(newConfig);
  }, [setGameConfig, initializeGame]);

  return (
    <div className="keyboard w-full max-w-md mx-auto">
      {KEYBOARD_KEYS.map((row, rowIndex) => (
        <div key={rowIndex} className="keyboard-row">
          {(
            rowIndex === 0 && isRandomMode && isGameComplete
              ? ['q', 'w', 'e', 'new-game', 'i', 'o', 'p']
              : rowIndex === 1 && isGameComplete
                ? ['a', 's', 'see-results', 'k', 'l']
                : row
          ).map((key) => {
            if (key === 'new-game') {
              return (
                <button
                  key={key}
                  onClick={handleNewRandomGame}
                  className="new-game-key"
                  aria-label="New game"
                >
                  NEW GAME
                </button>
              );
            }

            if (key === 'see-results') {
              return (
                <button
                  key={key}
                  onClick={handleShowResults}
                  className="see-results-key"
                  aria-label="See results"
                >
                  SEE RESULTS
                </button>
              );
            }

            const evaluation = keyboardState[key];
            
            // Determine key type for styling
            const isEnterKey = key === 'enter';
            const isBackspaceKey = key === 'backspace';
            
            return (
              <button
                key={key}
                onClick={() => handleClick(key)}
                className={cn(
                  evaluation,
                  isBackspaceKey && 'backspace-key',
                  isEnterKey && 'enter-key',
                  isKeyboardDisabled && 'opacity-70',
                  shouldShowHintLetter(gameMode, hintLetter) &&
                    hintLetter === key &&
                    evaluation !== 'correct' &&
                    `${isDarkMode ? '!bg-fourdle-yellow-dark' : '!bg-fourdle-yellow'}`
                )}
                disabled={isKeyboardDisabled}
                aria-label={key}
              >
                {key === 'backspace' ? '←' : key}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default Keyboard;
